import Component, {ComponentOptions} from 'molbio/maps/linear/Component';
import {FontAvailability, getLetterSpacingCorrection} from 'molbio/maps/MonospaceFontCorrection';
import {DNASlice} from 'molbio/maps/selectables/DNA';
import {Selection} from 'molbio/maps/selectables/Selection';
import {AlignmentMapPreferences} from 'molbio/maps/SequenceMapConstants';
import {TabState} from 'Navigation';
import preferences from 'Preferences';

import {DNA} from './LinearLayout';
import Slice from './Slice';
import {SliceView} from './SliceView';

export interface ResiduesViewOptions extends ComponentOptions {
  svgSupportsForeignObject: boolean;
  parent: SliceView;
  slice: Slice;
  selection: Selection;
}

export interface DNASliceDatum extends DNASlice {
  visible: boolean;
}

abstract class ResiduesView extends Component {
  abstract layout: DNA;
  svgSupportsForeignObject!: boolean;
  tabstate!: TabState;
  parent!: SliceView;
  slice!: Slice;
  selection!: Selection;

  initialize(options: ResiduesViewOptions): void {
    super.initialize(options);
    this.svgSupportsForeignObject = options.svgSupportsForeignObject;
    this.tabstate = options.tabstate;
    this.parent = options.parent;
    this.slice = options.slice;
    this.selection = this.model.get('polymer').get('selection');
  }

  registerListeners(): void {
    super.registerListeners();
    this.listenTo(FontAvailability.get(), 'change:areFontsReady', () => {
      this.update(true);
    });
  }

  abstract update(trigger: boolean): void;

  splitResidues(residues: string): Array<string> {
    // Rendering bug occurs if residues.length > 2^16
    // See: https://code.google.com/p/chromium/issues/detail?id=361448
    const MAX_LENGTH = 60000;
    const slices = [];
    for (let i = 0; i < residues.length; i += MAX_LENGTH) {
      slices.push(residues.slice(i, i + MAX_LENGTH));
    }
    return slices;
  }

  abstract getStartOffset(d: DNASliceDatum): number;

  abstract getText(d: DNASliceDatum): string;

  appendTextRows(
    parent: d3.Selection<d3.BaseType, unknown, d3.BaseType, unknown>,
    data: [DNASliceDatum, DNASliceDatum] | []
  ): void {
    let TEXT_TAG;
    let TEXT_TRANSLATE_Y: number;
    let TSPAN_TAG;
    let TSPAN_TAG_WITH_NS;
    let EXTRA_HEIGHT;

    if (this.svgSupportsForeignObject) {
      TEXT_TAG = 'foreignObject';
      TEXT_TRANSLATE_Y = 0; // <foreignObject> elements are positioned relative to their top edge
      TSPAN_TAG = 'span';
      TSPAN_TAG_WITH_NS = 'xhtml:span';
      // We need to add to the height to avoid cutting off letters that go below the baseline in
      // in some browsers. This isn't important for layout, so just double the height.
      EXTRA_HEIGHT = this.layout.innerHeight();
    } else {
      // If foreignObject is not available (e.g. in IE or Inkscape), we render normal <tspan>s
      TEXT_TAG = 'text';
      // <text> elements are positioned relative to their // bottom edge
      TEXT_TRANSLATE_Y = this.layout.height();
      TSPAN_TAG = 'tspan';
      TSPAN_TAG_WITH_NS = 'svg:tspan';
      EXTRA_HEIGHT = 0;
    }

    // Select on .text-container instead of TEXT_TAG, as querySelector in old versions of WebKit (ie
    // Safari 7) can't select camelCased tags (ie foreignObject). See: https://bugs.webkit.org/show_bug.cgi?id=83438
    let root = parent.selectAll('.text-container').data(data);
    root = root.join(TEXT_TAG).classed('text-container', true);
    if (preferences.get(AlignmentMapPreferences.ALIGNMENTS_BASES_TEXT_USE_GEOMETRIC_PRECISION)) {
      root.style('text-rendering', 'geometricPrecision');
    }

    const tspan = root
      .selectAll(TSPAN_TAG)
      .data((d) => this.splitResidues(this.layout.get('visible') ? this.getText(d) : ''));
    tspan.join(TSPAN_TAG_WITH_NS).text((d) => d);

    const fontSize = this.layout.get('type').fontSize;
    let letterSpacing = this.layout.get('type').letterSpacing;
    if (FontAvailability.get().get('areFontsReady')) {
      letterSpacing += getLetterSpacingCorrection(fontSize);
    }

    root
      // Firefox requires explicit width/height for foreignObject
      .attr('width', this.slice.get('maxWidth') as number)
      .attr('height', this.layout.innerHeight() + EXTRA_HEIGHT)
      .attr('font-size', fontSize)
      .attr('letter-spacing', letterSpacing)
      .attr('transform', (d, i: number) => {
        const centreOffset = this.layout.get('type').letterSpacing / 2;
        const translateX = this.getStartOffset(d) + centreOffset;
        const translateY = TEXT_TRANSLATE_Y + i * (this.layout.height() + this.layout.get('spacer'));
        return `translate(${translateX}, ${translateY})`;
      });
  }
}

export default ResiduesView;
